import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Card, CardContent, Theme, Typography } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import scanQrCodeImage from "@shared/images/scanQrCode.webp";
import { PageContentWrapper } from "@shared/PageContentWrapper/PageContentWrapper";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        width: "100%",
        borderRadius: theme.spacing(2),
    },
    content: {
        flex: "0 0 auto",
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    titleContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    mainContent: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(4),
        alignItems: "flex-start",
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
    },
    imageContainer: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 300,
        maxWidth: 400,
        [theme.breakpoints.down("md")]: {
            minWidth: "auto",
            maxWidth: "100%",
            width: "100%",
        },
    },
    image: {
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: 420,
    },
    textContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        justifyContent: "center",
        alignItems: "center",
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(2),
        textAlign: "left",
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface EnrollDeviceStepProps {
    onBack: () => void;
    onContinue: () => void;
}

export const EnrollDeviceStep: React.FC<EnrollDeviceStepProps> = ({ onBack, onContinue }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    return (
        <PageContentWrapper size="wide">
            <Card className={classes.container} elevation={3}>
                <CardContent className={classes.content}>
                    <Box className={classes.titleContainer}>
                        <Typography variant="h6" className={classes.title}>
                            {t("modals:enrollmentDialog.enrollExistingStep.title")}
                        </Typography>
                    </Box>
                    <Box className={classes.mainContent}>
                        <Box className={classes.imageContainer}>
                            <img
                                src={scanQrCodeImage}
                                alt={t("modals:enrollmentDialog.enrollExistingStep.imageAlt")}
                                className={classes.image}
                            />
                        </Box>
                        <Box className={classes.textContainer}>
                            <Typography variant="body1" className={classes.description}>
                                {t("modals:enrollmentDialog.enrollExistingStep.description")}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
                <Box className={classes.actions}>
                    <Box className={classes.actionsLeft}>
                        <SecondaryButton onClick={onBack}>{t("common:button.goBack")}</SecondaryButton>
                    </Box>
                    <PrimaryButton onClick={onContinue}>{t("common:button.continue")}</PrimaryButton>
                </Box>
            </Card>
        </PageContentWrapper>
    );
};
